import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home, Calculator, Book, ArrowRight, TrendingUp, FileText, HelpCircle } from 'lucide-react';
import { getAllBlogPosts } from '../data/blogData';

const NotFound = () => {
  const recentBlogs = getAllBlogPosts().slice(0, 3);

  useEffect(() => {
    // Track 404 page views
    if (window.clarity) {
      window.clarity('set', 'page_404', window.location.pathname);
    }
  }, []);

  const popularPages = [
    {
      icon: <Calculator className="text-blue-600" />,
      title: 'Conceptual Estimate',
      description: 'Get instant AI-powered cost estimates for your construction project',
      link: '/conceptual-estimate',
      cta: 'Start Estimating'
    },
    {
      icon: <FileText className="text-green-600" />,
      title: 'Drawing Estimate',
      description: 'Upload your blueprints for detailed cost analysis',
      link: '/drawing-estimate',
      cta: 'Upload Drawings'
    },
    {
      icon: <TrendingUp className="text-purple-600" />,
      title: 'Quote Checker',
      description: 'Verify contractor quotes with AI-powered analysis',
      link: '/quote-checker',
      cta: 'Check Quote'
    }
  ];

  const projectTypes = [
    { label: 'Single Family Home', slug: 'single-family' },
    { label: 'Multi-Family Home', slug: 'multi-family' },
    { label: 'Home Improvements', slug: 'home-improvements' },
    { label: 'Kitchen Upgrade', slug: 'kitchen-upgrade' },
    { label: 'Bathroom Upgrade', slug: 'bathroom-upgrade' },
    { label: 'ADU Construction', slug: 'adu' }
  ];

  return (
    <>
      <Helmet>
        <title>Page Not Found | Cost to Construction</title>
        <meta name="description" content="The page you're looking for doesn't exist. Explore our construction cost calculators, guides, and tools to get accurate estimates for your project." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          
          {/* 404 Header */}
          <div className="text-center mb-12">
            <div className="mb-6">
              <span className="text-8xl sm:text-9xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                404
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Page Not Found
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Sorry, the page you're looking for doesn't exist or may have been moved. But don't worry—we have plenty of helpful resources to assist with your construction project!
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Home size={20} className="mr-2" />
                Go to Homepage
              </Link>
              <Link
                to="/blog"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200 shadow-md hover:shadow-lg border-2 border-blue-600"
              >
                <Book size={20} className="mr-2" />
                Read Our Guides
              </Link>
            </div>
          </div>

          {/* Popular Tools Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Try Our Popular Tools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {popularPages.map((page, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 p-6 border border-gray-100"
                >
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {page.icon}
                    </div>
                    <h3 className="ml-3 text-xl font-bold text-gray-900">
                      {page.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm">
                    {page.description}
                  </p>
                  <Link
                    to={page.link}
                    className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                  >
                    {page.cta}
                    <ArrowRight size={16} className="ml-2" />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Estimate by Project Type */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Get Instant Estimates by Project Type
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {projectTypes.map((project) => (
                <Link
                  key={project.slug}
                  to={`/estimate/${project.slug}/california`}
                  className="flex flex-col items-center p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 border border-gray-200 hover:border-blue-300 hover:shadow-md"
                >
                  <Calculator size={24} className="text-blue-600 mb-2" />
                  <span className="text-sm font-semibold text-gray-900 text-center">
                    {project.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Blog Posts */}
          {recentBlogs.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Latest Construction Guides
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {recentBlogs.map((blog) => (
                  <Link
                    key={blog.slug}
                    to={`/blog/${blog.slug}`}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-100 group"
                  >
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-600 mb-3">
                        <Book size={16} className="mr-2" />
                        <span>{blog.readTime} read</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {blog.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {blog.metaDescription}
                      </p>
                      <span className="inline-flex items-center text-blue-600 font-semibold text-sm">
                        Read Guide
                        <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-xl p-8 text-center text-white">
            <HelpCircle size={48} className="mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Need Help Finding Something?
            </h2>
            <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
              Our AI-powered construction cost calculator can help you get accurate estimates for any project type and location.
            </p>
            <Link
              to="/conceptual-estimate"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Calculator size={20} className="mr-2" />
              Get Free Estimate Now
            </Link>
          </div>

        </div>
      </div>
    </>
  );
};

export default NotFound;

